import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

import { BaseWrapper, Button, DrawerBodyWrapper, DrawerFooterWrapper, Input, SectionHeader, Select, TOAST_TYPE } from '@base'
import { ButtonsWrapper } from '../EditSupplierQuotes/styles'
import { strings } from '@constants'
import { Spacings, Text } from '@styles'
import { Delete, Edit } from '@material-ui/icons'
import { capitalizeWord } from '@utils'
import { showToast } from '@data/state/action'

export const ItemWrapper = styled(BaseWrapper)`
	padding: ${Spacings.SPACING_2B};
`

export const BiFieldWrapper = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	column-gap: ${Spacings.SPACING_3B};
`

export const ListWrapper = styled.div`
	margin-top: ${Spacings.SPACING_4B};
	display: flex;
	flex-direction: column;
	gap: ${Spacings.SPACING_3B};
`

export const ListItemWrapper = styled.div`
	padding: ${Spacings.SPACING_2B};
	border-radius: ${Spacings.SPACING_2B};
	display: grid;
	grid-template-columns: ${({ oneButton }) => (oneButton ? '6fr 1fr' : '6fr 1fr 1fr')};
	gap: ${Spacings.SPACING_3B};
	background: ${({ theme }) => theme.palette.background.searchgrey};
	& > div {
		padding-left: ${Spacings.SPACING_2B};
		align-content: center;
		font-size: ${Text.MEDIUM};
	}
`

const WarningWrapper = styled.div`
	font-size: ${Text.SMALL};
	text-align: right;
	color: ${({ theme }) => theme.palette.text.red};
`

const derivePaymentTerm = (data, term) => {
	const _state = {
		payment_term: {
			value: '',
		},
		tenure_in_days: {
			id: 'tenure-in-days',
			label: strings(['tenure', 'in', 'days']),
			placeholder: strings(['enter', 'tenure', 'in', 'days']),
			value: '',
			dirty: false,
		},
		percentage: {
			id: 'percentage',
			label: strings('percentage'),
			placeholder: strings(['enter', 'percentage']),
			value: '',
			dirty: false,
		},
		condition: {
			id: 'condition',
			label: strings('condition'),
			placeholder: strings('select', 'condition'),
			value: '',
			dirty: false,
		},
	}

	if (data) {
		_state.payment_term.value = data?.payment_term
		_state.tenure_in_days.value = data?.tenure_in_days
		_state.percentage.value = data?.percentage
		_state.condition.value = data?.condition
	} else if (term) {
		_state.payment_term = term
	}
	return _state
}

const paymentTermsTypes = [
	{ key: 'ADVANCE', label: 'ADVANCE' },
	{ key: 'BG', label: 'BG' },
	{ key: 'LC', label: 'LC' },
	{ key: 'POD', label: 'POD' },
	{ key: 'CREDIT', label: 'CREDIT' },
]

const conditionTypes = [
	{ key: 'ON_DELIVERY', label: 'ON DELIVERY' },
	{ key: 'AFTER_DELIVERY', label: 'AFTER DELIVERY' },
	{ key: 'BEFORE_UNLOADING', label: 'BEFORE UNLOADING' },
	{ key: 'AFTER_UNLOADING', label: 'AFTER UNLOADING' },
	{ key: 'ON_INVOICING', label: 'ON INVOICING' },
	{ key: 'AFTER_DISPATCH', label: 'AFTER DISPATCH' },
	{ key: 'AGAINST_PERFORMA_INVOICE', label: 'AGAINST PERFORMA INVOICE' },
]

const PaymentTermsList = ({ edit = false, data, isSupplier, callbackFunction, viewOnly }) => {
	const dispatch = useDispatch()
	const [term, setTerm] = useState(null)
	const [termList, setTermList] = useState(data ?? [])
	const [editFormIndex, setEditFormIndex] = useState(-1) // it will store the index of termList to be edited

	const [formState, setFormState] = useState(null)

	const updateFormState = (key) => (value) => {
		setFormState((_state) => ({
			..._state,
			[key]: {
				..._state[key],
				value,
			},
		}))
	}

	// sets the formState empty and chosen payment term null
	const clearState = () => {
		setFormState(null)
		setTerm(null)
	}

	// useEffect(() => {}, [enquiry])

	// to edit list item
	const editSingleTermHandler = (_paymentTerm, index) => {
		setTerm(_paymentTerm?.payment_term)
		setFormState(derivePaymentTerm(_paymentTerm))
		setEditFormIndex(index)
	}
	const deleteSingleTermHandler = (_paymentTerm, index) => {
		setTermList(termList.filter((item, idx) => idx !== index))
	}

	// api callback
	const editPaymentTerm = () => {
		callbackFunction(termList)
	}

	// api callback
	const submitPaymentTerms = () => {
		callbackFunction(termList)
		// dispatch(toggleDrawer(false))
	}

	// opens form after choosing a payment term
	const onSelectTerm = (val) => {
		if (termList.find((item) => item.payment_term === val)) {
			dispatch(showToast(true, strings('payment_term_already_exists'), { type: TOAST_TYPE.ERROR }))
			return
		}
		setTerm(val)
		setFormState(derivePaymentTerm(null, val))
		setEditFormIndex(-1)
	}

	const addPaymentToTermList = () => {
		if (formState?.percentage?.value === '' || formState?.percentage?.value < 1) {
			dispatch(showToast(true, 'Invalid Percentage!', { type: TOAST_TYPE.ERROR }))
			return
		}
		let _state = {
			payment_term: formState?.payment_term?.value ?? term,
			tenure_in_days: formState?.tenure_in_days?.value === '' ? 1 : formState?.tenure_in_days?.value,
			condition: formState?.condition?.value,
			percentage: formState?.percentage?.value,
		}
		setTermList((_s) => [..._s, _state])
		clearState()
	}

	const editTermList = () => {
		let _termList = termList
		_termList[editFormIndex] = {
			payment_term: formState?.payment_term?.value,
			condition: formState?.condition?.value,
			tenure_in_days: formState?.tenure_in_days?.value === '' ? 1 : formState?.tenure_in_days?.value,
			percentage: formState?.percentage?.value,
		}
		setTermList(_termList)
		clearState()
	}

	const checkDisabled = useMemo(() => {
		return formState !== null || termList?.reduce((acc, item) => item.percentage + acc, 0) !== 100
	}, [formState, termList])

	const PaymentTermItem = () => {
		return (
			<ItemWrapper>
				<SectionHeader>{}</SectionHeader>
				<BiFieldWrapper>
					<Input
						id={formState?.tenure_in_days?.id}
						type='number'
						label={formState?.tenure_in_days?.label}
						placeholder={formState?.tenure_in_days?.placeholder}
						value={formState?.tenure_in_days?.value}
						onChange={updateFormState('tenure_in_days')}
					/>
					<Input
						id={formState?.percentage?.id}
						type='number'
						label={formState?.percentage?.label}
						placeholder={formState?.percentage?.placeholder}
						value={formState?.percentage?.value}
						onChange={updateFormState('percentage')}
					/>
				</BiFieldWrapper>

				<Select
					id={formState?.condition?.id}
					label={formState?.condition?.label}
					placeholder={formState?.condition?.placeholder}
					data={conditionTypes}
					primaryKey='key'
					displayKey='label'
					value={conditionTypes.find((item) => item.key === formState?.condition?.value) ?? {}}
					onChange={(value) => updateFormState('condition')(value.key)}
				/>
				<ButtonsWrapper>
					<Button xs margin={`0 0 0 0`} type='secondary' variant='' onClick={clearState}>
						{strings('cancel')}
					</Button>
					{editFormIndex !== -1 ? (
						<Button xs margin={`0 0 0 auto`} onClick={editTermList}>
							{strings('edit')}
						</Button>
					) : (
						<Button xs margin={`0 0 0 auto`} onClick={addPaymentToTermList}>
							{strings('add')}
						</Button>
					)}
				</ButtonsWrapper>
			</ItemWrapper>
		)
	}

	return (
		<>
			<DrawerBodyWrapper>
				{!viewOnly && (
					<Select
						id='select-payment-term'
						placeholder={strings(['select', 'payment', 'term'])}
						data={paymentTermsTypes}
						primaryKey='key'
						displayKey='label'
						value={paymentTermsTypes.find((item) => item.key === term) ?? {}}
						disabled={false}
						onChange={(val) => onSelectTerm(val.key)}
					/>
				)}
				{checkDisabled && <WarningWrapper>{strings('payment_terms_should_be_100')}</WarningWrapper>}
				{term !== null && formState !== null && PaymentTermItem()}

				<ListWrapper>
					{termList?.map((item, index) => (
						<ListItemWrapper>
							<div>{`${item?.percentage}% ${capitalizeWord(item?.payment_term ?? '')} ${
								item?.condition
									? item?.condition
											?.split('_')
											?.map((item) => capitalizeWord(item ?? ''))
											?.join(' ')
									: ''
							} for ${item?.tenure_in_days} days`}</div>
							<Button
								xs
								variant=''
								type='secondary'
								disabled={formState !== null || viewOnly}
								onClick={() => editSingleTermHandler(item, index)}
							>
								<Edit />
							</Button>
							<Button
								xs
								variant='destructive'
								type='secondary'
								disabled={formState !== null || viewOnly}
								onClick={() => deleteSingleTermHandler(item, index)}
							>
								<Delete />
							</Button>
						</ListItemWrapper>
					))}
				</ListWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper>
				<ButtonsWrapper>
					{!edit ? (
						<Button small onClick={submitPaymentTerms} disabled={checkDisabled || viewOnly}>
							{strings('submit')}
						</Button>
					) : (
						<Button small onClick={editPaymentTerm} disabled={checkDisabled || viewOnly}>
							{strings('update')}
						</Button>
					)}
				</ButtonsWrapper>
			</DrawerFooterWrapper>
		</>
	)
}

export default PaymentTermsList
